import { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { validateServiceBusString } from "../utils/validators.util";
import { getDisplayEventName } from "../utils/schema.util";
import { PUBLISH_ENDPOINT } from "../utils/config";

interface PublishRequest {
  connectionString?: string;
  connectionName?: string;
  eventType: string;
  payload: Record<string, any>;
}

const MASKED_CONNECTION_DISPLAY = "*".repeat(40);

export function useEventForm() {
  const [serviceBusString, setServiceBusString] = useState("");
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [selectedConnectionName, setSelectedConnectionName] = useState<string | null>(null);
  const [connectionsDialogOpen, setConnectionsDialogOpen] = useState(false);
  const [connectionsHelpOpen, setConnectionsHelpOpen] = useState(false);

  const handleChange = useCallback((eventName: string, field: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [eventName]: { ...prev[eventName], [field]: value },
    }));
  }, []);

  const handlePublish = useCallback(
    async (eventName: string) => {
      const usingNamedConnection = selectedConnectionName !== null;

      if (!usingNamedConnection && !validateServiceBusString(serviceBusString)) {
        toast.warning("🔌 Whoops… your Service Bus isn't plugged in.", {
          position: "bottom-right",
        });
        return;
      }

      const request: PublishRequest = {
        eventType: getDisplayEventName(eventName),
        payload: formValues[eventName] || {},
        ...(usingNamedConnection
          ? { connectionName: selectedConnectionName! }
          : { connectionString: serviceBusString }),
      };

      try {
        const res = await axios.post(PUBLISH_ENDPOINT, request);
        if (res.status === 200) {
          toast.success("🚀 Event published!", { position: "bottom-right" });
          setSelectedEvent(null);
        }
      } catch (err) {
        console.error(err);
        toast.error("💥 Something broke", { position: "bottom-right" });
      }
    },
    [serviceBusString, formValues, selectedConnectionName]
  );

  const handleSelectConnection = useCallback((name: string) => {
    setSelectedConnectionName(name);
    setServiceBusString(MASKED_CONNECTION_DISPLAY);
    setConnectionsDialogOpen(false);
  }, []);

  const handleClearConnection = useCallback(() => {
    setSelectedConnectionName(null);
    setServiceBusString("");
  }, []);

  return {
    serviceBusString,
    setServiceBusString,
    formValues,
    selectedEvent,
    setSelectedEvent,
    selectedConnectionName,
    connectionsDialogOpen,
    setConnectionsDialogOpen,
    connectionsHelpOpen,
    setConnectionsHelpOpen,
    handleChange,
    handlePublish,
    handleSelectConnection,
    handleClearConnection,
  };
}
